import { useRef, useState } from 'react'

import { ApiError, api } from '../lib/api'
import type { Photo, PresignResponse } from '../lib/types'

const MAX_PHOTOS = 10

/**
 * Photo attach for the intake form (01 §3.2, up to 10).
 *
 * Uploads go **straight to object storage** with a presigned URL and then tell the API about
 * it; the file never passes through the API process. That keeps a 20 MB phone photo off the
 * API's memory and its request timeout, and it is why the attach step re-decodes and strips
 * the file server-side (08 §3) — nothing has validated those bytes until then.
 */
export function PhotoUpload({
  estimateId,
  photos,
  onChange,
}: {
  estimateId: string
  photos: Photo[]
  onChange: (photos: Photo[]) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(0)
  const [error, setError] = useState<string | null>(null)

  async function upload(file: File) {
    const presign = await api.post<PresignResponse>(`/estimates/${estimateId}/photos:presign`, {
      content_type: file.type || 'image/jpeg',
      bytes: file.size,
    })
    if (file.size > presign.max_bytes) {
      throw new Error(
        `“${file.name}” is too large (${Math.round(presign.max_bytes / 1e6)} MB max).`,
      )
    }
    const put = await fetch(presign.url, {
      method: presign.method,
      headers: presign.headers,
      body: file,
    })
    if (!put.ok) throw new Error(`Could not upload “${file.name}”.`)
    return api.post<Photo>(`/estimates/${estimateId}/photos`, {
      bucket: presign.bucket,
      key: presign.key,
    })
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return
    setError(null)
    const room = MAX_PHOTOS - photos.length
    const chosen = Array.from(files).slice(0, room)
    if (chosen.length < files.length) {
      setError(`You can attach up to ${MAX_PHOTOS} photos.`)
    }

    setBusy((n) => n + chosen.length)
    const added: Photo[] = []
    for (const file of chosen) {
      try {
        added.push(await upload(file))
      } catch (err) {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : String(err))
      } finally {
        setBusy((n) => n - 1)
      }
    }
    if (added.length) onChange([...photos, ...added])
    if (input.current) input.current.value = ''
  }

  async function remove(photo: Photo) {
    await api.delete(`/estimates/${estimateId}/photos/${photo.id}`)
    onChange(photos.filter((p) => p.id !== photo.id))
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={photos.length >= MAX_PHOTOS}
          className="rounded-btn border border-line-2 bg-white px-4 py-2.5 font-semibold text-ink disabled:opacity-50"
        >
          {photos.length ? 'Add more photos' : 'Add photos'}
        </button>
        <span className="text-sm text-muted">
          {photos.length}/{MAX_PHOTOS}
          {busy > 0 && ` · uploading ${busy}…`}
        </span>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        className="sr-only"
        onChange={(event) => void onFiles(event.target.files)}
      />

      <p className="m-0 text-sm text-muted">
        Wide shots of the whole room help most. No need to include people — we strip location data
        from every photo automatically.
      </p>

      {error && (
        <p role="alert" className="m-0 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
          {error}
        </p>
      )}

      {photos.length > 0 && (
        <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4">
          {photos.map((photo) => (
            <li key={photo.id} className="relative">
              <img
                src={photo.url}
                alt=""
                loading="lazy"
                className="aspect-square w-full rounded-img object-cover"
              />
              <button
                type="button"
                onClick={() => void remove(photo)}
                aria-label="Remove photo"
                className="absolute right-1.5 top-1.5 rounded-full bg-navy/85 px-2 py-0.5 text-sm font-bold text-white"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
